/**
 * 市民端投诉表单「示范」文字 · 前后端同一份契约真本（2026-09-23）。
 *
 * ════════ 这件事是怎么来的 ════════
 *
 * 主公 2026-09-23：「投诉的表单页下面的这个示例，在后台做成可以编辑的」。
 * 改前那段示范写死在两处：手机端 `citizen/src/pages/Submit.tsx`、PC 端 `citizen/src/pc/PcForm.tsx`，
 * 各抄一遍、逐字相同。协会想换一句，得改代码、重新构建、重新发布 —— 而且得记得改两处。
 *
 * 做法与「快速咨询开场白」（`consult-greeting-contract.ts`）同构：一条 `app_setting`、
 * 后台一块卡片单独保存、`op_log` 留痕、市民端读不到就回落默认值。
 *
 * ════════ 为什么允许换行，但限得比开场白紧 ════════
 *
 * 示范是**一段**举例的话，现行默认值就是一行。给到 3 行，够协会分成「买方一例 / 租客一例」
 * 这种写法；再多就把「证据材料」挤到首屏外了 —— 那一栏是必填项，比示范要紧。
 */

/** 配置项的键。`app_setting.key` 存的就是它，全库唯一。 */
export const COMPLAINT_EXAMPLE_SETTING_KEY = 'citizen.complaint_example';

/**
 * 示范字数上限（按**码点**数，与开场白、建议问题同一种数法）。
 * 现行默认值 77 字；250 字大约是手机端五六行，再长就不是示范是范文了。
 */
export const MAX_COMPLAINT_EXAMPLE_LEN = 250;

/** 最多几行。 */
export const MAX_COMPLAINT_EXAMPLE_LINES = 3;

/**
 * 兜底示范 —— 与改前 `Submit.tsx` / `PcForm.tsx` 里写死的那段**逐字一致**。
 *   ① 后台配置页「从没配过」时的初始内容；
 *   ② 市民端**拉不到配置时**的回落 —— 表单下面那块示范绝不能空着或显示错误。
 */
export const DEFAULT_COMPLAINT_EXAMPLE =
  '我是买方，我在锦江区XX花园买房，XX地产公司的中介XXX在带看时刻意隐瞒了房屋渗水问题，还口头承诺返佣，事后不认账，我要投诉他，并申请退回服务费XX元。';

/** 后台读写用的整份配置。 */
export interface ComplaintExampleConfig {
  example: string;
  /** 最后一次保存时间（ISO）。从没配过时为 null */
  updatedAt: string | null;
  /** 最后一次保存人（staff_id）。从没配过时为 null */
  updatedBy: string | null;
  /** 当前这份配置存在哪：postgres / json。给排障看，不影响业务 */
  store?: string;
  /** true = 库里没有这条配置，返回的是 `DEFAULT_COMPLAINT_EXAMPLE` 打底 */
  isDefault?: boolean;
}

/** 市民端公开只读那条路的出参。只给文字，不给修改人。 */
export interface PublicComplaintExample {
  example: string;
}

function isBannedControlChar(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  if (code === 10 || code === 13) return false; // 换行允许
  return code < 0x20 || code === 0x7f;
}

/** 按码点数字数。 */
export function complaintExampleLength(text: string): number {
  return Array.from(text).length;
}

/** 归一化：统一行尾为 LF、压掉多余空行、去首尾空白。**只整理排版，不改内容。** */
export function normalizeComplaintExample(text: string): string {
  return String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/**
 * 校验。通过返回 null，不通过返回**中文原因**（原样给工作人员看）。
 * ★ 请先 `normalizeComplaintExample` 再校验。
 * ★ **不允许为空**：空示范在表单上就是一块空框，那是故障态不是配置。
 */
export function validateComplaintExample(text: unknown): string | null {
  if (typeof text !== 'string') return '示范必须是文本';
  if (text.trim() === '') return '示范不能为空';
  if (text !== text.trim()) return '示范首尾不能有空白';
  for (const ch of text) {
    if (isBannedControlChar(ch)) return '示范不能包含换行以外的控制字符';
  }
  const n = complaintExampleLength(text);
  if (n > MAX_COMPLAINT_EXAMPLE_LEN) {
    return `示范不超过 ${MAX_COMPLAINT_EXAMPLE_LEN} 个字，当前有 ${n} 个字`;
  }
  const lines = text.split('\n').length;
  if (lines > MAX_COMPLAINT_EXAMPLE_LINES) {
    return `示范最多 ${MAX_COMPLAINT_EXAMPLE_LINES} 行，当前有 ${lines} 行`;
  }
  return null;
}

/** 存储里那坨 JSON → 示范文本。形状不对返回 null，**绝不猜**。 */
export function parseComplaintExample(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (value as Record<string, unknown>)['example'];
  if (typeof raw !== 'string') return null;
  const text = normalizeComplaintExample(raw);
  return validateComplaintExample(text) === null ? text : null;
}
